import { useTranslation } from 'react-i18next';
import { Box, Table, TableContainer, TableBody, TableRow } from '@mui/material';
import { useEffect, useMemo } from 'react';

import { TableActionBar, TableCell, TableHead } from 'common/components/Table';
import { FilterBar } from 'common/components/FilterBar';
import { IconButton } from 'common/components/IconButton';
import { Tooltip } from 'common/components/Tooltip';
import { Loader } from 'common/components/Loader';
import { Typography } from 'common/components/Typography';
import {
    IPaginationProps,
    ISearchProps,
    ITableSelectionProps,
    TFilterBarItem,
    TTableAction,
} from 'common/types';
import { Pagination } from 'common/components/Pagination';
import { TrainerTableRow } from 'user/components/TrainerTable/TrainerTableRow';
import { IUserListItemFragment } from 'graphql/types';
import { Checkbox } from 'common/components/Checkbox';

interface IProps {
    disabled?: boolean;
    disabledTrainerIds?: string[];
    isEditable?: boolean;
    loading?: boolean;
    noResultsLabel?: string;
    paginationTranslationKey?: string;
    paginationProps?: IPaginationProps;
    tableActions?: TTableAction[];
    selectionProps?: ITableSelectionProps<IUserListItemFragment>;
    trainers?: IUserListItemFragment[];
    searchProps?: ISearchProps;
    inDrawer?: boolean;
    onSelectTrainers?(selectedTrainers: string[]): void;
}

export const TrainerTable = ({
    disabled,
    disabledTrainerIds,
    isEditable = true,
    loading,
    noResultsLabel,
    paginationTranslationKey,
    paginationProps,
    tableActions,
    selectionProps,
    trainers,
    searchProps,
    inDrawer,
    onSelectTrainers,
}: IProps) => {
    const [translate] = useTranslation();
    const {
        filters,
        searchQueryParam,
        selectedFilters,
        handleSearch: onSearch = () => {},
        handleFilterSelect: onFilterSelect,
    } = searchProps || {};
    const { paginationSettings, setPage } = paginationProps || {};
    const {
        rows = [],
        isSelected,
        selected,
        selectableItems = [],
        onSelectAll,
    } = selectionProps || {};

    const selectedTrainerIds = useMemo(() => selected || [], [selected]);

    useEffect(() => {
        onSelectTrainers?.(selectedTrainerIds);
    }, [selectedTrainerIds, onSelectTrainers]);

    const actions = tableActions?.map(
        ({ tooltipTitle, iconPath, onClick }, index) => (
            <Tooltip key={index} title={tooltipTitle}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        ml: 'auto',
                    }}
                >
                    <IconButton
                        color="inherit"
                        disabled={!isSelected}
                        iconPath={iconPath}
                        iconSize="2.4rem"
                        onClick={() => {
                            onClick?.(selectedTrainerIds);
                        }}
                    />
                </Box>
            </Tooltip>
        )
    );

    const handleSearch = (searchValue: string) => {
        onSearch?.(searchValue);

        if (!!selected?.length) onSelectAll?.();
    };

    const handleFilterSelect = (selectedFilters: TFilterBarItem[]) => {
        onFilterSelect?.(selectedFilters);

        if (!!selected?.length) onSelectAll?.();
    };

    return (
        <>
            {!!searchProps && (
                <FilterBar
                    disabled={disabled}
                    filters={filters}
                    initialSearchValue={searchQueryParam}
                    initialSelected={selectedFilters}
                    placeholder={translate('filterBarPlaceholder')}
                    onSearch={handleSearch}
                    onSearchClear={() => handleSearch?.('')}
                    onSelect={handleFilterSelect}
                />
            )}

            {!loading && !!rows.length && (
                <TableContainer>
                    {isEditable && (
                        <TableActionBar
                            actions={actions}
                            inDrawer={inDrawer}
                            selectionProps={selectionProps}
                        />
                    )}

                    <Table>
                        <TableHead>
                            <TableRow>
                                {isEditable && (
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={false}
                                            disabled={
                                                isSelected ||
                                                !selectableItems.length ||
                                                loading ||
                                                disabled
                                            }
                                            onChange={onSelectAll}
                                        />
                                    </TableCell>
                                )}
                                <TableCell>{translate('name')}</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {rows?.map(({ index, onSelect, isSelected }) => {
                                const trainer = trainers?.[index] || null;

                                if (!trainer) return null;

                                const isDisabled = disabledTrainerIds?.some(
                                    (disabledTrainerId) =>
                                        disabledTrainerId === trainer?.id
                                );

                                return (
                                    <TrainerTableRow
                                        disabled={isDisabled || disabled}
                                        isEditable={!isDisabled}
                                        isSelectable={isEditable}
                                        isSelected={isSelected}
                                        key={trainer.id}
                                        trainer={trainer}
                                        onSelect={onSelect}
                                    />
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {loading && (
                <Box sx={{ position: 'relative', height: '100px', mt: 4 }}>
                    <Loader />
                </Box>
            )}

            {!loading && !trainers?.length && (
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Typography>
                        {noResultsLabel || translate('noOptionsText.trainers')}
                    </Typography>
                </Box>
            )}

            {!loading && !!paginationSettings && (
                <Box sx={{ mt: 4 }}>
                    <Pagination
                        page={paginationSettings.page}
                        pageAmount={paginationSettings.pageAmount}
                        totalsAmount={paginationSettings.count}
                        totalsText={translate(
                            paginationTranslationKey || 'trainer',
                            {
                                count: paginationSettings.count,
                            }
                        )}
                        onChange={(page: number) => {
                            setPage?.(page);
                        }}
                    />
                </Box>
            )}
        </>
    );
};
